<template>
    <!-- Upload Modal -->
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-bg-white tw-rounded-2xl tw-shadow-lg tw-overflow-hidden">
            <!-- Header -->
            <div class="tw-p-6 tw-border-b tw-border-gray-200">
                <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">Upload Documents</h2>
                <p class="tw-text-sm tw-text-gray-600 tw-mt-1">Add new documents to your collection</p>
            </div>

            <!-- Content -->
            <div class="tw-p-6">
                <!-- File Upload Area -->
                <div
                    @drop.prevent="handleFileDrop"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @click="triggerFileInput"
                    :class="[
                        'tw-relative tw-w-full tw-min-h-[200px] tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200 tw-mb-4',
                        isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400'
                    ]"
                >
                    <icon icon="solar:cloud-upload-bold-duotone" class="tw-text-4xl tw-text-gray-400 tw-mb-2" />
                    <p class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-1">
                        Drop a file here or click to upload
                    </p>
                    <p class="tw-text-sm tw-text-gray-500">
                        Supports PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX files up to 10MB
                    </p>
                    
                    <input
                        ref="fileInput"
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        @change="handleFileSelect"
                        class="tw-hidden"
                    />
                </div>

                <!-- Upload Progress -->
                <div v-if="isUploading && uploadingFile" class="tw-space-y-3 tw-mb-4">
                    <h3 class="tw-text-sm tw-font-medium tw-text-gray-900">Uploading file...</h3>
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                        <icon :icon="getFileIcon(uploadingFile.type)" class="tw-text-2xl tw-text-gray-400 tw-flex-shrink-0" />
                        <div class="tw-flex-1 tw-min-w-0">
                            <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{{ uploadingFile.name }}</p>
                            <div class="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2 tw-mt-1">
                                <div
                                    class="tw-bg-primary-500 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
                                    :style="{ width: uploadingFile.progress + '%' }"
                                ></div>
                            </div>
                            <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ Math.round(uploadingFile.progress) }}% complete</p>
                        </div>
                    </div>
                </div>

                <!-- Selected File Preview -->
                <div v-if="selectedFile && !isUploading" class="tw-mb-4">
                    <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-3">Selected file</h3>
                    
                    <!-- File Preview -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                        <icon :icon="getFileIcon(selectedFileType)" class="tw-text-2xl tw-text-gray-400 tw-flex-shrink-0" />
                        <div class="tw-flex-1 tw-min-w-0">
                            <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{{ selectedFile.name }}</p>
                            <p class="tw-text-xs tw-text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                        </div>
                        <button
                            @click="removeFile"
                            class="tw-text-gray-400 hover:tw-text-red-500 tw-flex-shrink-0"
                        >
                            <icon icon="solar:close-circle-bold" class="tw-text-lg" />
                        </button>
                    </div>

                    <!-- Document Form -->
                    <div class="tw-mt-4">
                        <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-3">Document details</h3>
                        <div class="tw-space-y-4">
                            <FormField
                                v-model="uploadForm.name"
                                type="text"
                                label="Name"
                                placeholder="Document name (max 255 characters)"
                                left-icon="solar:document-text-linear"
                                hide-errors
                                required
                            />
                            
                            <FormField
                                v-model="uploadForm.description"
                                type="textarea"
                                label="Description"
                                placeholder="Document description (max 255 characters)"
                                hide-errors
                                rows="3"
                                required
                            />
                            
                            <!-- Status Toggle -->
                            <div class="tw-space-y-2">
                                <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                                    <icon icon="solar:settings-linear" class="tw-text-base" />
                                    Status
                                </label>
                                <div class="tw-flex tw-items-center tw-gap-3 tw-p-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white">
                                    <!-- Active Button -->
                                    <button
                                        type="button"
                                        @click="uploadForm.status = 'active'"
                                        :class="[
                                            'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                            uploadForm.status === 'active' 
                                                ? 'tw-bg-green-500 tw-text-white tw-shadow-sm' 
                                                : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                        ]"
                                    >
                                        <icon 
                                            :icon="uploadForm.status === 'active' ? 'solar:check-circle-bold' : 'solar:check-circle-linear'" 
                                            class="tw-text-base" 
                                        />
                                        Active
                                    </button>
                                    
                                    <!-- Inactive Button -->
                                    <button
                                        type="button"
                                        @click="uploadForm.status = 'inactive'"
                                        :class="[
                                            'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                            uploadForm.status === 'inactive' 
                                                ? 'tw-bg-red-500 tw-text-white tw-shadow-sm' 
                                                : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                        ]"
                                    >
                                        <icon 
                                            :icon="uploadForm.status === 'inactive' ? 'solar:close-circle-bold' : 'solar:close-circle-linear'" 
                                            class="tw-text-base" 
                                        />
                                        Inactive
                                    </button>
                                </div>
                            </div>
                            
                            <!-- Thumbnail Upload (Optional) -->
                            <div class="tw-space-y-2">
                                <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                                    <icon icon="solar:image-linear" class="tw-text-base" />
                                    Thumbnail 
                                    <span class="tw-text-xs tw-text-gray-500 tw-font-normal">(Optional)</span>
                                </label>
                                
                                <!-- Thumbnail Upload Area -->
                                <div v-if="!selectedThumbnail">
                                    <div
                                        @drop.prevent="handleThumbnailDrop"
                                        @dragover.prevent="isDraggingThumbnail = true"
                                        @dragleave.prevent="isDraggingThumbnail = false"
                                        @click="triggerThumbnailInput"
                                        :class="[
                                            'tw-relative tw-w-full tw-h-24 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                            isDraggingThumbnail ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400'
                                        ]"
                                    >
                                        <icon icon="solar:gallery-add-linear" class="tw-text-2xl tw-text-gray-400" />
                                        <p class="tw-text-xs tw-text-gray-500 tw-text-center tw-px-2">
                                            Drop image or click to upload<br />
                                            JPG, PNG (max 2MB)
                                        </p>
                                        
                                        <input
                                            ref="thumbnailInput"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.gif"
                                            @change="handleThumbnailSelect"
                                            class="tw-hidden"
                                        />
                                    </div>
                                </div>
                                
                                <!-- Thumbnail Preview -->
                                <div v-else class="tw-relative tw-w-full tw-max-w-sm">
                                    <img
                                        :src="thumbnailPreview"
                                        :alt="selectedThumbnail.name"
                                        class="tw-w-full tw-h-32 tw-object-cover tw-rounded-lg tw-border tw-border-gray-200"
                                    />
                                    <div class="tw-absolute tw-bottom-2 tw-left-2 tw-bg-black tw-bg-opacity-50 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded">
                                        {{ formatFileSize(selectedThumbnail.size) }}
                                    </div>
                                    <button
                                        @click="removeThumbnail"
                                        class="tw-absolute tw-top-2 tw-right-2 tw-w-6 tw-h-6 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors tw-text-xs"
                                    >
                                        <icon icon="solar:close-linear" class="tw-text-sm" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-p-6 tw-bg-gray-50 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button
                    intent="white"
                    @click="onCancel"
                    :disabled="isUploading"
                >
                    Cancel
                </a-button>
                <a-button
                    intent="primary"
                    @click="uploadFiles"
                    :disabled="!selectedFile || isUploading"
                    :class="[(!selectedFile || isUploading) && '!tw-border-gray-300']"
                    :loading="isUploading"
                >
                    Upload
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'
import FormField from '@/components/form/FormField.vue'
import { create } from '@/api/documents'
import useAlert from '@/composables/useAlert'

// Props
defineProps({
    visible: {
        type: Boolean,
        default: false
    }
})

// Emits
const emit = defineEmits(['close', 'uploaded'])



// Reactive data
const isDragging = ref(false)
const selectedFile = ref(null)
const selectedFileType = ref('')
const selectedFilePreview = ref('')
const uploadingFile = ref(null)
const fileInput = ref(null)
const isUploading = ref(false)

// Thumbnail data
const selectedThumbnail = ref(null)
const thumbnailPreview = ref('')
const thumbnailInput = ref(null)
const isDraggingThumbnail = ref(false)

// Upload form
const uploadForm = ref({
    name: '',
    description: '',
    status: 'active'
})

// Methods
const onCancel = () => {
    resetForm()
    emit('close')
}

const resetForm = () => {
    selectedFile.value = null
    selectedFileType.value = ''
    selectedFilePreview.value = ''
    uploadingFile.value = null
    isUploading.value = false
    uploadForm.value = {
        name: '',
        description: '',
        status: 'active'
    }
    // Reset thumbnail
    selectedThumbnail.value = null
    thumbnailPreview.value = ''
    isDraggingThumbnail.value = false
    
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    if (thumbnailInput.value) {
        thumbnailInput.value.value = ''
    }
    isDragging.value = false
}

const triggerFileInput = () => {
    fileInput.value?.click()
}

const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedFile(file)
    }
}

const handleFileDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file) {
        processSelectedFile(file)
    }
}

const processSelectedFile = (file) => {
    // Validate file type based on server requirements
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
    const fileExtension = getFileExtension(file.name)
    
    if (!allowedExtensions.includes(fileExtension)) {
        useAlert('Please select a valid file type (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX)', 'danger')
        return
    }
    
    selectedFile.value = file
    selectedFileType.value = getFileType(file.name)
    selectedFilePreview.value = ''
    
    // Auto-fill name from filename
    if (!uploadForm.value.name) {
        uploadForm.value.name = file.name.split('.').slice(0, -1).join('.')
    }
}

const removeFile = () => {
    selectedFile.value = null
    selectedFileType.value = ''
    selectedFilePreview.value = ''
    uploadForm.value.name = ''
    uploadForm.value.description = ''
    uploadForm.value.status = 'active'
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

// Thumbnail methods
const triggerThumbnailInput = () => {
    thumbnailInput.value?.click()
}

const handleThumbnailSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processThumbnailFile(file)
    }
}

const handleThumbnailDrop = (event) => {
    isDraggingThumbnail.value = false
    const file = event.dataTransfer.files[0]
    if (file) {
        processThumbnailFile(file)
    }
}

const processThumbnailFile = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
        useAlert('Please select a valid image file (JPG, PNG, GIF)', 'danger')
        return
    }
    
    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024 // 2MB
    if (file.size > maxSize) {
        useAlert('Thumbnail image must be smaller than 2MB', 'danger')
        return
    }
    
    selectedThumbnail.value = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        thumbnailPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeThumbnail = () => {
    selectedThumbnail.value = null
    thumbnailPreview.value = ''
    if (thumbnailInput.value) {
        thumbnailInput.value.value = ''
    }
}

const uploadFiles = async () => {
    if (!selectedFile.value) return
    
    // Validate form
    if (!uploadForm.value.name.trim()) {
        useAlert('Please enter a document name', 'danger')
        return
    }
    
    if (uploadForm.value.name.length > 255) {
        useAlert('Document name cannot exceed 255 characters', 'danger')
        return
    }
    
    if (!uploadForm.value.description.trim()) {
        useAlert('Please enter a document description', 'danger')
        return
    }
    
    if (uploadForm.value.description.length > 255) {
        useAlert('Document description cannot exceed 255 characters', 'danger')
        return
    }
    
    isUploading.value = true
    uploadingFile.value = {
        id: Date.now(),
        name: selectedFile.value.name,
        type: selectedFileType.value,
        progress: 0
    }

    try {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append('file', selectedFile.value)
        formData.append('name', uploadForm.value.name.trim())
        formData.append('description', uploadForm.value.description.trim())
        formData.append('type', selectedFileType.value)
        formData.append('size', selectedFile.value.size.toString())
        formData.append('extension', getFileExtension(selectedFile.value.name))
        formData.append('mime_type', selectedFile.value.type || 'application/octet-stream')
        formData.append('status', uploadForm.value.status)
        
        // Add thumbnail if provided
        if (selectedThumbnail.value) {
            formData.append('thumbnail', selectedThumbnail.value)
        }
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
            if (uploadingFile.value && uploadingFile.value.progress < 90) {
                uploadingFile.value.progress += Math.random() * 20
            }
        }, 200)

        // Call the API
        const response = await create(formData)
        
        // Complete progress
        if (uploadingFile.value) {
            uploadingFile.value.progress = 100
        }
        
        clearInterval(progressInterval)
        
        // Create document object from response
        const newDoc = {
            id: response.data.id || Date.now(),
            name: selectedFile.value.name,
            type: selectedFileType.value,
            size: selectedFile.value.size,
            uploadDate: new Date(),
            preview: selectedFileType.value === 'image' ? selectedFilePreview.value : '/assets/images/avatars/avatar.png',
            ...response.data
        }
        
        // Show success message
        useAlert('Document uploaded successfully!', 'success')
        
        // Emit uploaded event with new document
        emit('uploaded', newDoc)
        
        // Reset form and close modal
        setTimeout(() => {
            resetForm()
            emit('close')
        }, 500)
        
    } catch (error) {
        console.error('Upload failed:', error)
        
        // Clear progress
        uploadingFile.value = null
        
        // Show error message
        const errorMessage = error.response?.data?.message || 'Upload failed. Please try again.'
        useAlert(errorMessage, 'danger')
        
    } finally {
        isUploading.value = false
    }
}

const getFileIcon = (type) => {
    const iconMap = {
        pdf: 'solar:document-text-bold-duotone',
        doc: 'solar:document-bold-duotone',
        spreadsheet: 'solar:chart-square-bold-duotone',
        presentation: 'solar:presentation-graph-bold-duotone',
        document: 'solar:file-bold-duotone'
    }
    return iconMap[type] || iconMap.document
}

const getFileExtension = (fileName) => {
    return fileName.split('.').pop().toLowerCase()
}

const getFileType = (fileName) => {
    const extension = getFileExtension(fileName)
    const typeMap = {
        pdf: 'pdf',
        doc: 'doc',
        docx: 'doc',
        xls: 'spreadsheet',
        xlsx: 'spreadsheet',
        ppt: 'presentation',
        pptx: 'presentation'
    }
    return typeMap[extension] || 'document'
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
</script>

<style scoped>
</style> 